<template>
  <div class="game-view" :class="{ 'bonus-level': isBonusLevel }">
    <div v-if="isBonusLevel" class="bonus-banner">
      🎉 BONUS LEVEL! 🎉
    </div>
    <h2>{{ gameTitle }}</h2>
    
    <div class="game-status">
      <p><strong>Current Level:</strong> {{ gameResult.currentLevel }} <span v-if="isBonusLevel" class="bonus-badge">BONUS</span></p>
      <p><strong>Accumulated Prize:</strong> {{ gameResult.accumulatedPrize }} SEK</p>
      <p v-if="gameResult.isWin !== undefined"><strong>Last Result:</strong> <span :class="gameResult.isWin ? 'win-text' : 'loss-text'">{{ gameResult.isWin ? '✅ WIN' : '❌ LOSS' }}</span></p>
      <p v-if="gameResult.canContinue !== undefined"><strong>Can Continue:</strong> {{ gameResult.canContinue ? 'Yes' : 'No' }}</p>
      <p v-if="gameResult.tickets && gameResult.tickets.length > 0"><strong>Total Levels Played:</strong> {{ gameResult.tickets.length }}</p>
    </div>

    <div class="action-buttons">
      <button class="button cashout-btn" @click="$emit('cashout')" :disabled="!gameResult.canCheckout">
        💰 Cashout - {{ gameResult.accumulatedPrize || 0 }} SEK
      </button>
      <button class="button continue-btn" @click="$emit('continue')" :disabled="gameResult.mustFinalize || !gameResult.canContinue">
        ▶️ Continue to Next Level
      </button>
    </div>

    <div v-if="lastResult" class="result-message" :class="lastResult.win ? 'win' : 'loss'">
      {{ lastResult.message }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'CashoutGameView',
  props: {
    gameResult: {
      type: Object,
      required: true
    }
  },
  emits: ['continue', 'cashout'],
  data() {
    return {
      gameType: 'cashout',
      lastResult: null
    }
  },
  watch: {
    gameResult(newVal) {
      this.initializeGame(newVal);
    }
  },
  computed: {
    gameTitle() {
      const titles = {
        'doubling': 'Doubling Game - 50% chance to double your win',
        'ladder': 'Ladder Game - 8 levels with increasing difficulty',
        'bonus': 'Bonus Game - Unlock bonus multipliers'
      };
      return titles[this.gameType] || 'Cashout Game';
    },
    isBonusLevel() {
      // Check if current game result indicates we're on a bonus level
      // isBonusLevel is set to true when the player just won and the next level will be bonus
      return this.gameResult && this.gameResult.isBonusLevel === true;
    }
  },
  mounted() {
    if (this.gameResult) {
      this.initializeGame(this.gameResult);
    }
  },
  methods: {
    initializeGame(result) {
      // Simple initialization - all data comes from API in gameResult prop
      this.gameType = 'cashout';
      this.lastResult = null;
      console.log('Game initialized with result:', result);
    }
  }
}
</script>

<style scoped>
.game-view {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  color: white;
  position: relative;
}

.game-view.bonus-level {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.9; }
}

.bonus-banner {
  background: rgba(255, 215, 0, 0.9);
  color: #000;
  padding: 10px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border-radius: 8px;
  margin-bottom: 15px;
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.bonus-badge {
  background: gold;
  color: #000;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 14px;
  margin-left: 10px;
  animation: blink 1.5s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.win-text {
  color: #4caf50;
  font-weight: bold;
}

.loss-text {
  color: #f44336;
  font-weight: bold;
}

.game-status {
  background: rgba(0, 0, 0, 0.3);
  padding: 15px;
  border-radius: 8px;
  margin: 20px 0;
}

.game-status p {
  margin: 10px 0;
  font-size: 18px;
  font-weight: bold;
}

.game-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 30px 0;
  justify-content: center;
}

.game-button {
  padding: 15px 30px;
  font-size: 16px;
  font-weight: bold;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  min-width: 200px;
}

.game-button:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
}

.game-button:active {
  transform: scale(0.98);
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin: 30px 0;
  flex-wrap: wrap;
}

.button {
  padding: 12px 25px;
  font-size: 16px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.cashout-btn {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.cashout-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
}

.continue-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.continue-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button:disabled:hover {
  transform: none;
  box-shadow: none;
}

.result-message {
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  animation: slideIn 0.3s ease-in-out;
}

.result-message.win {
  background-color: rgba(76, 175, 80, 0.8);
  color: white;
}

.result-message.loss {
  background-color: rgba(244, 67, 54, 0.8);
  color: white;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
